import { useEffect, useState, useRef, useMemo } from "react";
import {
  MenuItem,
  Grid,
  InputLabel,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";
import Select from "@mui/material/Select";
import { getDefaultPackSettings } from "./PackSettingsDialog";
import { getNumber } from "src/utils/cardUtils";
import useTenantCompetitions from "src/hooks/useTenantCompetitions";
import SPORT_POSITIONS from "src/utils/sportPositions";
import PositionFields from "./PositionFields";

// Export validation function for use in parent component
export const validateGradePull = (gradePull) => {
  // gradePull is optional, so if not provided, it's valid
  if (!gradePull) return { isValid: true, error: null };

  // Must be an array
  if (!Array.isArray(gradePull) || gradePull.length === 0) {
    return { isValid: false, error: "Grade pull must be an array with at least one object" };
  }

  // Validate each object in the array
  for (let i = 0; i < gradePull.length; i++) {
    const item = gradePull[i];
    
    // Each item must have a "Card" property
    if (!item || typeof item !== "object" || !item.Card) {
      return { isValid: false, error: `Item at index ${i} must have a "Card" property` };
    }

    const card = item.Card;
    
    // Card must have Base, Silver, Gold, AllStar properties
    if (typeof card !== "object" || !card.hasOwnProperty("Base") || !card.hasOwnProperty("Silver") || !card.hasOwnProperty("Gold") || !card.hasOwnProperty("AllStar")) {
      return { isValid: false, error: `Card at index ${i} must have Base, Silver, Gold, and AllStar properties` };
    }

    // Calculate sum (convert strings to numbers, empty string = 0)
    const base = parseFloat(card.Base) || 0;
    const silver = parseFloat(card.Silver) || 0;
    const gold = parseFloat(card.Gold) || 0;
    const allStar = parseFloat(card.AllStar) || 0;
    const sum = base + silver + gold + allStar;

    // Sum must equal 100
    if (sum !== 100) {
      return { isValid: false, error: `Card at index ${i}: Base + Silver + Gold + AllStar must equal 100 (currently ${sum})` };
    }
  }

  return { isValid: true, error: null };
};

// Local validation function (same logic)
const validateGradePullLocal = validateGradePull;

export default function MarketplacePackSettings(props) {
  const { packSettings: propsPackSettings, packSettingsChangeHandler, onValidationChange, cardQuantity } = props;
  const [packSettings, setPackSettings] = useState(null);
  const [totalCard, setTotalCard] = useState(0);
  const [gradePullError, setGradePullError] = useState(null);
  const { tenantCompetitions } = useTenantCompetitions();
  const isInitialized = useRef(false);

  // Get sport from selected competition
  const selectedCompetition = tenantCompetitions.find(
    (c) => +c.competitionId === +packSettings?.competitionId
  );
  const sport = selectedCompetition?.sport?.toLowerCase() || "hockey";

  const positionFields = useMemo(() => {
    const safeSport =
    SPORT_POSITIONS[sport] ? sport : "hockey";
    return ["Team", ...SPORT_POSITIONS[safeSport], "Random"];
  }, [sport]);

  const handlerRef = useRef();
  handlerRef.current = packSettingsChangeHandler;

  const validationRef = useRef();
  validationRef.current = onValidationChange;

  useEffect(() => {
    if (isInitialized.current) return;
    if (tenantCompetitions.length === 0) return;
    if (propsPackSettings === null) {
      const firstCompetitionSport = tenantCompetitions[0]?.sport?.toLowerCase() || "";
      const defaultSettings = getDefaultPackSettings(firstCompetitionSport);
      defaultSettings.competitionId = tenantCompetitions[0].competitionId;
      setPackSettings(defaultSettings);
      handlerRef.current(defaultSettings);
    } else {
      setPackSettings(propsPackSettings);
    }
    isInitialized.current = true;
  }, [propsPackSettings, tenantCompetitions]);

  useEffect(() => {
    if (packSettings && handlerRef.current && packSettings !== propsPackSettings) {
      handlerRef.current(packSettings);
    }
  }, [packSettings, propsPackSettings]);

  const sumFields = (obj, fields = []) =>
    fields.reduce((total, key) => total + getNumber(+obj?.[key]), 0);

  useEffect(() => {
    if (!packSettings || !validationRef.current) return;

    const positionTotal = sumFields(packSettings, positionFields);
    const ratingTotal =
      getNumber(+packSettings?.AllStar) +
      getNumber(+packSettings?.Base) +
      getNumber(+packSettings?.Gold) +
      getNumber(+packSettings?.Silver);

    setTotalCard(positionTotal);

    // Validate: position total must equal rating total (gradePull is independent)
    const totalsMatch = positionTotal === ratingTotal;
    const isValid = totalsMatch;

    // Validate gradePull separately if provided
    if (packSettings?.gradePull) {
      const gradePullValidation = validateGradePullLocal(packSettings.gradePull);
      setGradePullError(gradePullValidation.error);
    } else {
      setGradePullError(null);
    }

    // Notify parent of validation status
    validationRef.current(isValid, positionTotal);
  }, [packSettings, positionFields]);

  const handleGradePullChange = (value) => {
    try {
      const parsed = value.trim() === "" ? null : JSON.parse(value);
      setPackSettings((prevState) => ({
        ...prevState,
        gradePull: parsed,
      }));
      
      // Validate the parsed gradePull
      if (parsed) {
        const validation = validateGradePullLocal(parsed);
        setGradePullError(validation.error);
      } else {
        setGradePullError(null);
      }
    } catch (error) {
      console.error("Invalid JSON in gradePull:", error);
      setGradePullError("Invalid JSON format");
    }
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setPackSettings((prevState) => ({
      ...prevState,
      [id]: +value,
    }));
  };

  const handleCompetitionIdChange = (value) => {
    const competition = tenantCompetitions.find(c => +c.competitionId === +value);
    const sport = competition?.sport?.toLowerCase() || "";
    const defaultSettings = getDefaultPackSettings(sport);
    defaultSettings.competitionId = value;
    setPackSettings(defaultSettings);
    handlerRef.current(defaultSettings);
  };

  return (
    <>
      {packSettings !== null && packSettings.competitionId && (
        <Grid container>
          <Grid item xs={15} sx={{ mt: 0, p: 1 }}>
            <Typography variant="h6">
              These settings take precedence over settings defined for each pack
              type. <br />
              Total cards across positions must equal total cards across
              ratings. <br />
              Grade pull percentages (if specified) take precedence over number
              of cards for each rating.
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ mt: 0, p: 1 }}>
            <Typography variant="body1">
              Number of Cards: {totalCard}
              {!isNaN(cardQuantity) && totalCard !== cardQuantity && (
                <Typography component="span" color="error" sx={{ ml: 2 }}>
                  (Must equal Card Quantity: {cardQuantity})
                </Typography>
              )}
            </Typography>
          </Grid>
          <Grid item xs={3} sx={{ m: 0, p: 1 }}>
            <TextField
              id="Gold"
              label="Gold"
              fullWidth
              value={packSettings.Gold}
              variant="standard"
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={3} sx={{ m: 0, p: 1 }}>
            <TextField
              id="Silver"
              label="Silver"
              fullWidth
              value={packSettings.Silver}
              variant="standard"
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={3} sx={{ m: 0, p: 1 }}>
            <TextField
              id="Base"
              label="Base"
              fullWidth
              value={packSettings.Base}
              variant="standard"
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={3} sx={{ m: 0, p: 1 }}>
            <TextField
              id="AllStar"
              label="AllStar"
              fullWidth
              value={packSettings.AllStar}
              variant="standard"
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={3} sx={{ m: 0, p: 1 }}>
            <TextField
              id="Team"
              label="Team"
              fullWidth
              value={packSettings.Team}
              variant="standard"
              onChange={handleChange}
            />
          </Grid>
          <PositionFields
  positions={SPORT_POSITIONS[sport]}
  packSettings={packSettings}
  handleChange={handleChange}
  variant="standard"
/>
          <Grid item xs={3} sx={{ m: 0, p: 1 }}>
            <TextField
              id="Random"
              label="Random"
              fullWidth
              value={packSettings.Random}
              variant="standard"
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={3} sx={{ m: 0, p: 1 }}>
            <TextField
              id="gradePull"
              label="Grade Pull %"
              fullWidth
              multiline
              rows={8}
              value={packSettings.gradePull ? JSON.stringify(packSettings.gradePull, null, 2) : ""}
              variant="standard"
              onChange={(e) => handleGradePullChange(e.target.value)}
              error={!!gradePullError}
              helperText={gradePullError || "Optional: Array of objects with Card property. Each Card's Base+Silver+Gold+AllStar must sum to 100."}
            />
          </Grid>
          <Grid item xs={9} sx={{ m: 0, p: 1 }}>
            <FormControl sx={{ m: 1, minWidth: 320 }} size="small">
              <InputLabel id="demo-simple-select-label">Competition</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={packSettings.competitionId}
                label="Competition"
                onChange={(event) =>
                  handleCompetitionIdChange(event.target.value)
                }
              >
                {tenantCompetitions.map((competition) => (
                  <MenuItem
                    value={competition.competitionId}
                    key={`competition-${competition.competitionId}`}
                  >
                    {competition.competitionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}
    </>
  );
}
